import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import { connect } from 'react-redux';
import {
  reduxForm, Field, getFormValues, change,
} from 'redux-form';
import { formatDate, downloadFile } from '../../../utils/func';
import { isRequired, dateRangeValidate } from '../../../utils/validator';
import PrimaryButton from '../../../lib/ui/buttons/PrimaryButton';
import SecondaryButton from '../../../lib/ui/buttons/SecondaryButton';
import DatePicker from '../../../lib/ui/form_elements/DatePicker';
import Checkbox from '../../../lib/ui/form_elements/Checkbox';
import { LABEL_COLUMNS_TRANSLATION_KEY, today } from '../utils';
import { apiGetQuoteReports } from '../../../services/quotes';
import {
  ModalTitlesContainer, ModalTitle, ModalSubTitle, CategoriesContainer, CategoryContainer, CategoryTitle, BoxContainer, BoxInfo,
  DownloadModalBody, DatesContainer, ButtonGroup, ModalContainer,
} from '../css';

const NUMBER_OF_DAYS = 60;

const formName = 'ManageOrdersDownload';
const BOX_CONTAINER_MAX_HEIGHT = '145px';

let DownloadModalContent = ({
  formData = {}, dispatch, valid, columns, accountNumbers, closeModal,
}) => {
  const {
    filterFromDate, filterToDate, filterAccountNumbers, filterColumns,
  } = formData;

  // set all selected columns on the download modal
  useEffect(() => {
    dispatch(change(formName, 'filterColumns', columns));
  }, []);

  const allAccountNumbersSelected = accountNumbers
    && accountNumbers.length > 0
    && filterAccountNumbers
    && filterAccountNumbers.length === accountNumbers.length;

  const handleSelectAllAccountNumbers = (event) => {
    if (event.target.checked) {
      dispatch(change(formName, 'filterAccountNumbers', accountNumbers.map((a) => a.value)));
    } else {
      dispatch(change(formName, 'filterAccountNumbers', []));
    }
  };

  const handleDownloadSubmit = (event) => {
    event.preventDefault();
    if (!valid) {
      return;
    }

    const downloadAccountNumbers = filterAccountNumbers.filter((n) => n !== '');
    if (!downloadAccountNumbers || downloadAccountNumbers.length === 0) {
      return;
    }

    const downloadfilterColumns = filterColumns.slice(1);

    apiGetQuoteReports(downloadAccountNumbers, downloadfilterColumns, filterFromDate, filterToDate).then((res) => {
      if (res.status === 200) {
        const { fileName, mimeType, fileDataAsBase64 } = res.data;
        const fileType = fileName.includes('.csv') ? '' : '.csv';
        downloadFile({ dataAsBase64: fileDataAsBase64, mimeType, filename: fileName + fileType });
        closeModal();
      }
    });
  };

  return (
    <form onSubmit={handleDownloadSubmit}>
      <ModalContainer>
        <ModalTitlesContainer>
          <ModalTitle>{t('DownloadQuoteReportTitle')}</ModalTitle>
          <ModalSubTitle>{t('DownloadQuoteReportDescription')}</ModalSubTitle>
        </ModalTitlesContainer>

        <DownloadModalBody>
          <DatesContainer>
            <Field
              name="filterFromDate"
              label={t('FromDate')}
              component={DatePicker}
              maxDate={today}
              validate={[isRequired, dateRangeValidate]}
            />
            <Field
              name="filterToDate"
              label={t('ToDate')}
              component={DatePicker}
              maxDate={today}
              validate={[isRequired, dateRangeValidate]}
            />
          </DatesContainer>

          <CategoriesContainer>
            <CategoryContainer>
              <CategoryTitle>{t('BillingAccount')}</CategoryTitle>
              <BoxContainer maxHeight={BOX_CONTAINER_MAX_HEIGHT}>
                <BoxInfo>
                  <Checkbox
                    input={{
                      value: allAccountNumbersSelected,
                      onChange: handleSelectAllAccountNumbers,
                    }}
                    label={t('SelectAll')}
                  />
                  <Field
                    name="filterAccountNumbers"
                    component={Checkbox}
                    options={accountNumbers}
                    multiple
                  />
                </BoxInfo>
              </BoxContainer>
            </CategoryContainer>

            <CategoryContainer>
              <CategoryTitle>{t('Columns')}</CategoryTitle>
              <BoxContainer maxHeight={BOX_CONTAINER_MAX_HEIGHT}>
                <BoxInfo>
                  <Field
                    name="filterColumns"
                    component={Checkbox}
                    options={columns.filter((e) => e !== 'actions').map((column) => ({
                      label: t(`Column${LABEL_COLUMNS_TRANSLATION_KEY[column]}`),
                      value: column,
                    }))}
                    multiple
                  />
                </BoxInfo>
              </BoxContainer>
            </CategoryContainer>
          </CategoriesContainer>
        </DownloadModalBody>

        <ButtonGroup>
          <SecondaryButton
            label={t('Cancel')}
            onClick={closeModal}
            showArrow={false}
          />
          <PrimaryButton
            type="submit"
            label={t('Download')}
            disabled={!valid || (!filterAccountNumbers || !filterAccountNumbers.length)}
          />
        </ButtonGroup>
      </ModalContainer>
    </form>
  );
};

const mstp = (state) => ({
  formData: getFormValues(formName)(state),
  initialValues: {
    filterFromDate: formatDate(new Date((new Date()).getTime() - (NUMBER_OF_DAYS * 24 * 60 * 60 * 1000))),
    filterToDate: formatDate(new Date()),
    filterAccountNumbers: state.profile.accountNumbers,
    filterColumns: [],
  },
});

DownloadModalContent.propTypes = {
  formData: PropTypes.object,
  valid: PropTypes.bool,
  columns: PropTypes.array,
  accountNumbers: PropTypes.array,
  closeModal: PropTypes.func,
  dispatch: PropTypes.func,
};

DownloadModalContent = reduxForm({
  form: formName,
})(DownloadModalContent);

export default connect(mstp)(DownloadModalContent);
